import { createClient } from "graphql-ws";
import { decodeToken } from "react-jwt";
import { SubscribeFunction } from "react-relay-network-modern";
import { Observable } from "relay-runtime";
import { ReduxStore } from "@store/store.redux";
import { getValidAppJwt, supabase } from "./supabase-client";
import { wsApiBase } from "../environment";

type JwtClaimData = {
	accountId: string;
};

const RECONNECT_DELAY_MS = 3000;

const reconnectListeners = new Set<() => void>();

/**
 * Notifies when a subscription's socket re-establishes a connection after having lost it.
 * A resumed subscription does not replay events missed while it was down, so consumers should
 * use this to reconcile their state (e.g. refetch a query) instead of trusting the stream alone.
 */
export const onWSReconnected = (listener: () => void): (() => void) => {
	reconnectListeners.add(listener);
	return () => reconnectListeners.delete(listener);
};

const notifyReconnected = () => {
	reconnectListeners.forEach((listener) => listener());
};

const formatSubscriptionError = (err: any): Error => {
	if (Array.isArray(err)) return new Error(err.map(({ message }) => message).join(", "));
	if (err instanceof CloseEvent) {
		return new Error(`Socket closed with event ${err.code} ${err.reason || ""}`);
	}
	return err instanceof Error ? err : new Error(String(err));
};

export const WSSubscription: SubscribeFunction = (operation, variables) => {
	return Observable.create((sink) => {
		if (!operation.text) {
			sink.error(new Error("Operation text cannot be empty"));
			return;
		}
		const query = operation.text;

		let cancelled = false;
		let unsubscribe: (() => void) | null = null;
		let reconnectTimeout: ReturnType<typeof setTimeout> | undefined;
		let hasConnectedBefore = false;

		function scheduleReconnect(error: unknown) {
			if (cancelled) return;
			// eslint-disable-next-line no-console
			console.error("[WSSubscription] reconnecting after error", error);
			clearTimeout(reconnectTimeout);
			reconnectTimeout = setTimeout(connect, RECONNECT_DELAY_MS);
		}

		function connect() {
			supabase.auth
				.getSession()
				.then(({ data: { session } }) => {
					if (cancelled) return;

					if (!session) return scheduleReconnect(new Error("No active session"));

					const appJwt = ReduxStore.getState().auth.appJwt;
					const accountId = appJwt
						? decodeToken<JwtClaimData>(appJwt)?.accountId
						: undefined;

					if (!accountId) {
						return scheduleReconnect(new Error("Account ID not found in token"));
					}

					const subscriptionClient = createClient({
						url: `${wsApiBase}/api/graphql/ws`,
						connectionParams: async () => ({
							token: (await supabase.auth.getSession()).data.session?.access_token,
							tkaToken: await getValidAppJwt(),
							accountId,
						}),
						retryAttempts: 3,
						keepAlive: 10000,
						lazy: true,
						on: {
							connected: () => {
								if (hasConnectedBefore) notifyReconnected();
								hasConnectedBefore = true;
							},
						},
					});

					unsubscribe = subscriptionClient.subscribe(
						{
							operationName: operation.name,
							query,
							variables,
						},
						{
							...sink,
							error: (err: any) => scheduleReconnect(formatSubscriptionError(err)),
						},
					);
				})
				.catch(scheduleReconnect);
		}

		connect();

		return () => {
			cancelled = true;
			clearTimeout(reconnectTimeout);
			unsubscribe?.();
		};
	}) as any;
};
